import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";

interface ImageInformation {
  job_name: string;
  iteration: number;
  slice_number?: string;
  dimension?: string;
}

/**
 * A single shot marker, positioned as a fraction of the image (0–1,
 * left→right, top→bottom). Mirrors the merged shot shape rendered live by
 * XwaiSliceViewerComponent (ShotOverlayDto + source location), so the flipper
 * tooltip can show the same fields.
 */
export interface ShotOverlayPoint {
  x: number;
  y: number;
  color: string;
  shot_id: number;
  traces_fit: number;
  sx: number;
  sy: number;
  sz: number;
}

export interface ImageFlipperServiceType {
  image_information: ImageInformation;
  image_link: string;
  shot_overlay?: ShotOverlayPoint[];
}

@Injectable({
  providedIn: "root",
})
export class ImageFlipperService {
  private imagesSubject = new BehaviorSubject<ImageFlipperServiceType[]>([]);
  images$ = this.imagesSubject.asObservable();

  // Drawer visibility is shared so other UI (e.g. the sidebar's toggle) can
  // open/close the same flipper instance the FAB controls.
  private drawerVisibleSubject = new BehaviorSubject<boolean>(false);
  drawerVisible$ = this.drawerVisibleSubject.asObservable();

  private savedConvoKeySubject = new BehaviorSubject<string | null>(null);
  saved_convo_key$ = this.savedConvoKeySubject.asObservable();

  private refreshConversationsSubject = new BehaviorSubject<boolean>(false);
  refresh_conversations$ = this.refreshConversationsSubject.asObservable();

  private newChatSubject = new Subject<void>();
  new_chat$ = this.newChatSubject.asObservable();

  // Fires when the chat finishes producing an answer. Hosts (e.g. the model-page
  // panel) use this to badge their toggle when a response lands while collapsed.
  private answerCompleteSubject = new Subject<void>();
  answer_complete$ = this.answerCompleteSubject.asObservable();

  constructor() {}

  // Signal that the assistant has finished an answer.
  notify_answer_complete() {
    this.answerCompleteSubject.next();
  }

  add_image(image: ImageFlipperServiceType, shot_overlay?: ShotOverlayPoint[]) {
    const currentImages = this.imagesSubject.value;
    const updatedImages = [...currentImages, { ...image, shot_overlay }];
    this.imagesSubject.next(updatedImages);
  }

  clear_images() {
    this.imagesSubject.next([]);
  }

  openDrawer() {
    this.drawerVisibleSubject.next(true);
  }

  closeDrawer() {
    this.drawerVisibleSubject.next(false);
  }

  toggleDrawer() {
    this.drawerVisibleSubject.next(!this.drawerVisibleSubject.value);
  }

  remove_image_at_index(index: number) {
    const currentImages = this.imagesSubject.value;
    const updatedImages = currentImages.filter((_, i) => i !== index);
    this.imagesSubject.next(updatedImages);
  }

  // this is only the hack for showing the saved conversations
  update_saved_convo_key(key: string | null) {
    this.savedConvoKeySubject.next(key);
  }

  // Trigger sidebar to refresh conversations list
  trigger_conversations_refresh() {
    this.refreshConversationsSubject.next(true);
  }

  // Ask the chat to start a brand-new conversation (fresh thread + cleared messages)
  trigger_new_chat() {
    this.newChatSubject.next();
  }
}
